import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/shadcn/avatar';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/shadcn/command';
import { Dialog, DialogContent } from '@/components/ui/shadcn/dialog';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { csrfFetch } from '@/lib/csrfFetch';
import { cn, formatDateTime, logger } from '@/lib/utils';
import { Attachment, CommentCreatedEvent, User } from '@/types';
import { Link } from '@inertiajs/react';
import { formatDistanceToNow } from 'date-fns';
import { FileText, Loader2, Maximize2, Paperclip, Send, X } from 'lucide-react';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

/** Breathing room between the composer and the bottom of the viewport. */
const PANEL_BOTTOM_GUTTER = 24;
/** Below this the thread is unusable, so it scrolls the page instead. */
const MIN_PANEL_HEIGHT = 320;

type CommentApi = {
    id: string | number; // UUID (string) or negative number for optimistic comments
    user_id: string | number; // UUID (string) or number for optimistic comments
    body: string;
    type: string;
    metadata?: Record<string, unknown>;
    created_at: string;
    user?: User;
    attachments?: Attachment[];
    attachments_with_urls?: Attachment[];
};

interface UniversalCommentsProps {
    model: string;
    entityId: string | number;
    users: User[];
    currentUser?: User;
    /**
     * 'viewport' (default) fills the screen from wherever the panel starts — for a
     * full tab or page. 'container' fills its parent instead, for embedding in a
     * card whose height is set by the surrounding layout.
     */
    layout?: 'viewport' | 'container';
}

export default function UniversalComments({ model, entityId, users, currentUser, layout = 'viewport' }: UniversalCommentsProps) {
    const isMobile = useIsMobile();
    const { t } = useTranslations();
    const [comments, setComments] = useState<CommentApi[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [newComment, setNewComment] = useState<string>('');
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [mentionOpen, setMentionOpen] = useState(false);
    const [mentionSearch, setMentionSearch] = useState('');
    const [mentionStartPos, setMentionStartPos] = useState<number | null>(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxContent, setLightboxContent] = useState<{ type: 'image' | 'pdf'; url: string; name: string } | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [panelHeight, setPanelHeight] = useState<number | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const mentionListRef = useRef<HTMLDivElement>(null);

    // Handle click outside to close mention list
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (mentionOpen && mentionListRef.current && !mentionListRef.current.contains(event.target as Node)) {
                setMentionOpen(false);
                setMentionStartPos(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [mentionOpen]);

    const sortCommentsByCreatedAt = (items: CommentApi[]) => {
        return [...items].sort((a, b) => {
            const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
            const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;

            return aTime - bTime;
        });
    };

    const fetchComments = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetch(`/comments?model=${encodeURIComponent(model)}&id=${encodeURIComponent(String(entityId))}`, {
                headers: { Accept: 'application/json' },
            });
            if (!res.ok) {
                throw new Error('Failed to fetch comments');
            }
            const data = await res.json();
            const incomingComments = Array.isArray(data?.data) ? data.data : [];
            setComments(sortCommentsByCreatedAt(incomingComments));
        } catch (error) {
            console.error('Error fetching comments:', error);
            toast.error(t('toasts.comments_universalcomments.load_failed', 'Failed to load comments'));
        } finally {
            setLoading(false);
        }
    }, [model, entityId]);

    useEffect(() => {
        fetchComments();
    }, [fetchComments]);

    // Auto-scroll to bottom when new comments arrive
    useEffect(() => {
        if (scrollAreaRef.current) {
            // Use requestAnimationFrame for smooth scrolling
            requestAnimationFrame(() => {
                if (scrollAreaRef.current) {
                    scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
                }
            });
        }
    }, [comments]);

    // Real-time comment handling
    useEffect(() => {
        if (!window.Echo) {
            logger.log('UniversalComments: Echo not available');
            return;
        }

        if (!currentUser) {
            logger.log('UniversalComments: No current user, skipping subscription');
            return;
        }

        // Subscribe to entity-specific channel
        const channelName = `entity.${model}.${entityId}`;
        logger.log('UniversalComments: Subscribing to channel:', channelName);
        logger.log('UniversalComments: Model:', model, 'EntityId:', entityId, 'CurrentUser:', currentUser.id);

        const channel = window.Echo.private(channelName);

        // Add connection status listeners
        channel.subscribed(() => {
            logger.log('UniversalComments: Successfully subscribed to channel:', channelName);
        });

        channel.error((error: ErrorEvent) => {
            console.error('UniversalComments: Channel subscription error:', error);
            logger.error('UniversalComments: Channel error details:', error);
        });

        // Listen for the CommentCreated event
        channel.listen('CommentCreated', (data: CommentCreatedEvent) => {
            logger.log('UniversalComments: Received CommentCreated event:', data);
            // Add new comment to the list only if it doesn't already exist
            const newComment: CommentApi = {
                id: data.id, // Keep as string (UUID)
                user_id: data.user_id, // Keep as string (UUID)
                body: data.body,
                type: data.type,
                metadata: data.metadata,
                attachments: data.attachments,
                attachments_with_urls: data.attachments_with_urls,
                created_at: data.created_at,
                user: data.user,
            };

            setComments((prev) => {
                // Check if comment already exists by ID to prevent duplicates
                const existsById = prev.some((comment) => String(comment.id) === String(newComment.id));
                if (existsById) {
                    logger.log('UniversalComments: Comment already exists, skipping:', newComment.id);
                    return prev;
                }

                // Check if this is replacing an optimistic comment
                // Match by: same user, same body, and optimistic comment exists
                const optimisticIndex = prev.findIndex((comment) => {
                    if (typeof comment.id !== 'number' || comment.id >= 0) {
                        return false; // Not an optimistic comment
                    }
                    // Match by user ID (handle both string and number)
                    const commentUserId = String(comment.user_id);
                    const newCommentUserId = String(newComment.user_id);
                    const userIdMatch = commentUserId === newCommentUserId;

                    // Match by body content
                    const bodyMatch = comment.body === newComment.body;

                    // Match by time (within 5 seconds)
                    const timeMatch = Math.abs(new Date(comment.created_at).getTime() - new Date(newComment.created_at).getTime()) < 5000;

                    return userIdMatch && bodyMatch && timeMatch;
                });

                if (optimisticIndex !== -1) {
                    logger.log('UniversalComments: Replacing optimistic comment with real one');
                    // Replace optimistic comment with real one
                    const updated = [...prev];
                    updated[optimisticIndex] = newComment;
                    return sortCommentsByCreatedAt(updated);
                }

                logger.log('UniversalComments: Adding new comment from real-time event');
                // New comment from another user or different comment
                return sortCommentsByCreatedAt([...prev, newComment]);
            });
        });

        // Return cleanup function
        return () => {
            logger.log('UniversalComments: Unsubscribing from channel:', channelName);
            try {
                channel.stopListening('CommentCreated');
                window.Echo.leaveChannel(`private-${channelName}`);
            } catch (error) {
                logger.error('UniversalComments: Error during cleanup:', error);
            }
        };
    }, [model, entityId, currentUser]);

    const handleMention = (user: User) => {
        if (mentionStartPos === null || !textareaRef.current) {
            return;
        }

        const mentionText = `@${user.first_name} ${user.last_name}`;
        const textBefore = newComment.substring(0, mentionStartPos);
        const textAfter = newComment.substring(textareaRef.current.selectionStart);
        const newText = textBefore + mentionText + ' ' + textAfter;

        setNewComment(newText);
        setMentionOpen(false);
        setMentionSearch('');
        setMentionStartPos(null);

        setTimeout(() => {
            textareaRef.current?.focus();
            const newPos = mentionStartPos + mentionText.length + 1;
            textareaRef.current?.setSelectionRange(newPos, newPos);
        }, 0);
    };

    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        const cursorPos = e.target.selectionStart;
        setNewComment(value);

        // Check if @ was just typed
        const textBefore = value.substring(0, cursorPos);
        const lastChar = textBefore[cursorPos - 1];

        if (lastChar === '@') {
            setMentionStartPos(cursorPos - 1);
            setMentionSearch('');
            setMentionOpen(true);
        } else if (mentionStartPos !== null) {
            // We're in a mention context
            const mentionText = textBefore.substring(mentionStartPos + 1);
            // Check if we've left the mention context (space, newline, etc.)
            if (/\s/.test(lastChar) || cursorPos <= mentionStartPos) {
                setMentionOpen(false);
                setMentionStartPos(null);
                setMentionSearch('');
            } else {
                setMentionSearch(mentionText);
            }
        } else {
            setMentionOpen(false);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (selectedFiles.length + files.length > 5) {
            toast.error(t('toasts.comments_universalcomments.max_files', 'Maximum 5 files allowed'));
            return;
        }
        setSelectedFiles((prev) => [...prev, ...files]);
    };

    const removeFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) {
            return bytes + ' B';
        }
        if (bytes < 1024 * 1024) {
            return (bytes / 1024).toFixed(1) + ' KB';
        }
        return (bytes / 1024 / 1024).toFixed(1) + ' MB';
    };

    const filteredUsers = users.filter((user) => {
        const searchLower = mentionSearch.toLowerCase();
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
        return fullName.includes(searchLower) || user.email.toLowerCase().includes(searchLower);
    });

    const renderCommentBody = (body: string) => {
        if (!body) {
            return null;
        }

        // Check if body contains HTML tags
        const hasHtml = /<[^>]+>/.test(body);

        // If HTML, extract text content for mention parsing
        let textContent = body;
        if (hasHtml && typeof window !== 'undefined' && window.DOMParser) {
            try {
                const parser = new window.DOMParser();
                const doc = parser.parseFromString(body, 'text/html');
                textContent = doc.body.textContent || body;
            } catch {
                // Fallback to original body if parsing fails
                textContent = body.replace(/<[^>]*>/g, '');
            }
        }

        // Parse mentions properly - look for @Name pattern
        const mentionRegex = /@([A-Za-z]+(?:\s+[A-Za-z]+)?)/g;
        const parts: Array<{ type: 'text' | 'mention'; content: string; user?: User }> = [];
        let lastIndex = 0;
        let match;

        while ((match = mentionRegex.exec(textContent)) !== null) {
            // Add text before mention
            if (match.index > lastIndex) {
                parts.push({ type: 'text', content: textContent.substring(lastIndex, match.index) });
            }

            // Add mention
            const mentionName = match[1].trim();
            const mentionedUser = users.find((u) => `${u.first_name} ${u.last_name}` === mentionName);
            parts.push({
                type: 'mention',
                content: match[0],
                user: mentionedUser,
            });

            lastIndex = mentionRegex.lastIndex;
        }

        // Add remaining text
        if (lastIndex < textContent.length) {
            parts.push({ type: 'text', content: textContent.substring(lastIndex) });
        }

        // If no mentions found, return the original content (handles HTML or plain text)
        if (parts.length === 0) {
            if (hasHtml) {
                return <span dangerouslySetInnerHTML={{ __html: body }} />;
            }
            return <span>{body}</span>;
        }

        return parts.map((part, index) => {
            if (part.type === 'mention') {
                const badge = <Badge variant="secondary">{part.content}</Badge>;
                const uniqueKey = `mention-${index}-${part.content}-${part.user?.id || 'no-user'}`;

                if (part.user) {
                    return (
                        <Link key={uniqueKey} href={`/employee/${part.user.id}`} onClick={(e) => e.stopPropagation()}>
                            {badge}
                        </Link>
                    );
                }
                return <span key={uniqueKey}>{badge}</span>;
            }
            const uniqueKey = `text-${index}-${part.content.substring(0, 20)}`;
            return <span key={uniqueKey}>{part.content}</span>;
        });
    };

    const openLightbox = (attachment: Attachment) => {
        const url = 'url' in attachment && attachment.url ? attachment.url : `/storage/${attachment.path}`;
        if (attachment.type === 'image') {
            setLightboxContent({ type: 'image', url, name: attachment.name });
        } else if (attachment.mime === 'application/pdf') {
            setLightboxContent({ type: 'pdf', url, name: attachment.name });
        }
        setLightboxOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newComment.trim() && selectedFiles.length === 0) {
            return;
        }

        if (!currentUser) {
            toast.warning(t('toasts.comments_universalcomments.login_required', 'You must be logged in to comment'));
            return;
        }

        // Store comment data before clearing form
        const commentBody = newComment;
        const commentFiles = [...selectedFiles];

        // Create optimistic comment IMMEDIATELY (before API call) for instant feedback
        const tempId = -Date.now(); // Negative ID for optimistic comment
        const optimisticComment: CommentApi = {
            id: tempId,
            user_id: currentUser.id, // Keep as string UUID, don't convert to number
            body: commentBody,
            type: 'general',
            created_at: new Date().toISOString(),
            user: currentUser, // Ensure user object is set
            attachments:
                commentFiles.length > 0
                    ? commentFiles.map((file) => ({
                          type: file.type.startsWith('image/') ? 'image' : 'file',
                          path: '', // Will be updated when real comment arrives
                          name: file.name,
                          size: file.size,
                          mime: file.type,
                          url: URL.createObjectURL(file), // Temporary URL for optimistic display
                      }))
                    : undefined,
        };

        // Add optimistic comment immediately for instant UI update
        setComments((prev) => {
            const updated = sortCommentsByCreatedAt([...prev, optimisticComment]);
            // Scroll to bottom after state update
            setTimeout(() => {
                if (scrollAreaRef.current) {
                    scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
                }
            }, 0);
            return updated;
        });

        // Clear form immediately for better UX
        setNewComment('');
        setSelectedFiles([]);
        setIsSubmitting(true);

        try {
            // Create FormData for file uploads
            const formData = new FormData();
            formData.append('model', model);
            formData.append('id', String(entityId));
            formData.append('body', commentBody);
            formData.append('type', 'general');

            // Add files to FormData
            commentFiles.forEach((file) => {
                formData.append('attachments[]', file);
            });

            const res = await csrfFetch('/comments', {
                method: 'POST',
                body: formData,
            });

            if (!res.ok) {
                const result = await res.json().catch(() => ({}));
                // Remove optimistic comment on error
                setComments((prev) => prev.filter((comment) => comment.id !== tempId));
                throw new Error(result?.message || 'Failed to add comment');
            }

            // Real-time event will replace the optimistic comment
            // Fallback: If real-time event doesn't arrive within 5 seconds, refetch comments and remove optimistic
            setTimeout(() => {
                setComments((prev) => {
                    // Check if optimistic comment still exists (wasn't replaced by real-time event)
                    const stillOptimistic = prev.some((comment) => comment.id === tempId);
                    if (stillOptimistic) {
                        // Remove optimistic comment and refetch
                        const filtered = prev.filter((comment) => comment.id !== tempId);
                        fetchComments();
                        return filtered;
                    }
                    return prev;
                });
            }, 5000);

            // Clear timeout if component unmounts or comment is replaced
            // (The real-time event handler will replace the optimistic comment)

            toast.success(t('toasts.comments_universalcomments.comment_added', 'Comment added successfully'));
        } catch (error) {
            console.error('Error submitting comment:', error);
            toast.error(error instanceof Error ? error.message : 'Failed to add comment');
        } finally {
            setIsSubmitting(false);
        }
    };

    const isEmbedded = layout === 'container';
    const hasText = newComment.trim().length > 0;
    const canSubmit = hasText || selectedFiles.length > 0;
    const shouldShowSend = isMobile && !isEmbedded ? hasText : true;

    /**
     * On desktop the panel fills the viewport from wherever it starts and scrolls
     * internally. Its offset is measured rather than assumed, because the headers
     * above it differ per host page (and wrap at intermediate widths) — a fixed
     * `100vh - Xrem` pushed the composer off-screen whenever a page grew a row.
     */
    useLayoutEffect(() => {
        if (isMobile || layout === 'container') {
            setPanelHeight(null);

            return;
        }

        const measure = () => {
            const element = panelRef.current;

            if (!element) {
                return;
            }

            // Distance from the top of the document, so scrolling does not affect it.
            const offsetTop = element.getBoundingClientRect().top + window.scrollY;

            setPanelHeight(Math.max(MIN_PANEL_HEIGHT, window.innerHeight - offsetTop - PANEL_BOTTOM_GUTTER));
        };

        measure();

        window.addEventListener('resize', measure);

        // Catches header reflow (e.g. action buttons wrapping) without a resize event.
        const observer = new ResizeObserver(measure);
        observer.observe(document.body);

        return () => {
            window.removeEventListener('resize', measure);
            observer.disconnect();
        };
    }, [isMobile, layout]);

    return (
        <>
            <div
                ref={panelRef}
                className={cn('relative flex flex-col', (isMobile || layout === 'container') && 'h-full min-h-0')}
                style={isMobile || layout === 'container' ? undefined : { height: panelHeight ? `${panelHeight}px` : undefined }}
            >
                {/* Comments List - Scrollable */}
                <div className={cn('min-h-0 flex-1 overflow-y-auto pb-2', isEmbedded ? 'px-6' : 'pr-4')} ref={scrollAreaRef}>
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
                        </div>
                    ) : comments.length === 0 ? (
                        <div className="text-muted-foreground flex items-center justify-center py-8 text-sm">
                            No comments yet. Be the first to comment!
                        </div>
                    ) : (
                        <div className="space-y-4 pb-4">
                            {comments.map((comment) => {
                                // Robust user ID comparison - handle both string UUIDs and number IDs
                                const commentUserId = String(comment.user_id);
                                const currentUserId = currentUser ? String(currentUser.id) : null;
                                const isCurrentUser = currentUserId !== null && commentUserId === currentUserId;

                                // Find user for display - prefer comment.user, fallback to users array
                                const commentUser = comment.user || users.find((u) => String(u.id) === commentUserId);

                                return (
                                    <div key={String(comment.id)} className={cn('flex gap-3', isCurrentUser && 'flex-row-reverse')}>
                                        <Avatar className="h-8 w-8 shrink-0">
                                            <AvatarImage src={commentUser?.avatar} />
                                            <AvatarFallback className="text-xs">
                                                {commentUser?.first_name?.[0]}
                                                {commentUser?.last_name?.[0]}
                                            </AvatarFallback>
                                        </Avatar>

                                        <div className={cn('min-w-0 flex-1 space-y-2', isCurrentUser && 'items-end')}>
                                            <div
                                                className={cn(
                                                    'max-w-[80%] rounded-lg px-4 py-2',
                                                    isCurrentUser ? 'bg-primary text-primary-foreground ml-auto' : 'bg-muted',
                                                )}
                                            >
                                                <div className="mb-1 flex items-center gap-2">
                                                    <span className="text-sm font-semibold">
                                                        {commentUser?.first_name} {commentUser?.last_name}
                                                    </span>
                                                    {commentUser?.position && (
                                                        <Badge variant="outline" className={cn('bg-accent text-primary border text-xs')}>
                                                            {commentUser.position}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div
                                                    className={cn(
                                                        'text-sm break-words whitespace-pre-wrap',
                                                        isCurrentUser ? 'text-primary-foreground' : '',
                                                    )}
                                                >
                                                    {renderCommentBody(comment.body)}
                                                </div>
                                            </div>

                                            {/* Attachments */}
                                            {(comment.attachments_with_urls || comment.attachments) &&
                                                (comment.attachments_with_urls || comment.attachments)!.length > 0 && (
                                                    <div className={cn('flex flex-wrap gap-2', isCurrentUser && 'justify-end')}>
                                                        {(comment.attachments_with_urls || comment.attachments)!.map((attachment, idx) => (
                                                            <div
                                                                key={`attachment-${String(comment.id)}-${idx}-${attachment.name || attachment.path || idx}`}
                                                                className="group bg-background hover:border-primary relative cursor-pointer overflow-hidden rounded-lg border transition-colors"
                                                                onClick={() => openLightbox(attachment)}
                                                            >
                                                                {attachment.type === 'image' &&
                                                                (('url' in attachment && attachment.url) || attachment.path) ? (
                                                                    <div className="relative">
                                                                        <img
                                                                            src={
                                                                                'url' in attachment && attachment.url
                                                                                    ? attachment.url
                                                                                    : `/storage/${attachment.path}`
                                                                            }
                                                                            alt={attachment.name}
                                                                            className="h-32 w-32 object-cover"
                                                                        />
                                                                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                                                                            <Maximize2 className="h-5 w-5 text-white" />
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="hover:bg-accent flex h-full min-w-[200px] items-center gap-2 rounded-md p-3 transition-colors">
                                                                        <FileText className="text-muted-foreground h-5 w-5 shrink-0" />
                                                                        <div className="min-w-0 flex-1">
                                                                            <p className="truncate text-sm font-medium">{attachment.name}</p>
                                                                            <p className="text-muted-foreground text-xs">
                                                                                {formatFileSize(attachment.size)}
                                                                            </p>
                                                                        </div>
                                                                        <Maximize2 className="text-muted-foreground h-4 w-4 shrink-0" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                            <div
                                                className={cn(
                                                    'text-muted-foreground flex items-center gap-2 text-xs',
                                                    isCurrentUser && 'justify-end',
                                                )}
                                            >
                                                <span>{formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}</span>
                                                <span>•</span>
                                                <span>{formatDateTime(comment.created_at)}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Clears the viewport-fixed composer; embedded, the composer is in flow. */}
                {!isEmbedded && <div className="h-[60px] md:hidden" />}

                {/* Composer */}
                <div
                    className={cn(
                        'bg-background z-20 border-t py-3',
                        isEmbedded ? 'px-6' : 'px-4',
                        // Embedded: flush with the bottom edge of the host card, on every
                        // breakpoint. A viewport-fixed composer would float over the page
                        // (and sit under the mobile action bar).
                        isEmbedded
                            ? 'shrink-0'
                            : isMobile
                              ? 'fixed right-0 bottom-0 left-0 pb-[max(env(safe-area-inset-bottom),0.5rem)]'
                              : 'sticky bottom-0',
                    )}
                >
                    <form onSubmit={handleSubmit} className="space-y-3">
                        {/* Selected Files Preview - Absolute on mobile to sit above input */}
                        {selectedFiles.length > 0 && (
                            <div
                                className={cn(
                                    'flex flex-wrap gap-2',
                                    isMobile && !isEmbedded
                                        ? 'bg-background/95 absolute right-0 bottom-full left-0 border-t p-2 backdrop-blur'
                                        : 'mb-2',
                                )}
                            >
                                {selectedFiles.map((file, index) => (
                                    <div
                                        key={`selected-file-${file.name}-${file.size}-${index}`}
                                        className="group bg-muted relative rounded-lg border p-2"
                                    >
                                        {file.type.startsWith('image/') ? (
                                            <div className="relative">
                                                <img src={URL.createObjectURL(file)} alt={file.name} className="h-20 w-20 rounded object-cover" />
                                                <Button
                                                    type="button"
                                                    variant="destructive"
                                                    size="icon"
                                                    className="absolute -top-2 -right-2 h-5 w-5 rounded-full opacity-0 transition-opacity group-hover:opacity-100"
                                                    onClick={() => removeFile(index)}
                                                >
                                                    <X className="h-3 w-3" />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <FileText className="text-muted-foreground h-5 w-5" />
                                                <div className="min-w-0">
                                                    <p className="max-w-[100px] truncate text-xs font-medium">{file.name}</p>
                                                    <p className="text-muted-foreground text-xs">{formatFileSize(file.size)}</p>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-5 w-5"
                                                    onClick={() => removeFile(index)}
                                                >
                                                    <X className="h-3 w-3" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="flex items-end gap-2">
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                onChange={handleFileSelect}
                                className="hidden"
                            />

                            {/* Attachment bubble (left) */}
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="relative h-9 w-9 shrink-0 rounded-full"
                                disabled={isSubmitting || selectedFiles.length >= 5}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Paperclip className="h-4 w-4" />
                                {selectedFiles.length > 0 && (
                                    <span className="bg-primary text-primary-foreground absolute -top-1 -right-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-medium">
                                        {selectedFiles.length}
                                    </span>
                                )}
                            </Button>

                            {/* Input (single-line) + mentions */}
                            <div className="relative min-w-0 flex-1">
                                {mentionOpen && (
                                    <div
                                        ref={mentionListRef}
                                        className="bg-popover animate-in fade-in slide-in-from-bottom-2 absolute bottom-full left-0 z-50 mb-2 w-full max-w-[300px] overflow-hidden rounded-md border shadow-md"
                                    >
                                        <Command>
                                            <CommandList className="max-h-[200px] overflow-y-auto">
                                                {filteredUsers.length === 0 ? (
                                                    <CommandEmpty className="text-muted-foreground py-3 text-center text-sm">
                                                        No users found.
                                                    </CommandEmpty>
                                                ) : (
                                                    <CommandGroup>
                                                        {filteredUsers.map((user) => (
                                                            <CommandItem
                                                                key={user.id}
                                                                onSelect={() => handleMention(user)}
                                                                className="cursor-pointer"
                                                            >
                                                                <Avatar className="mr-2 h-6 w-6">
                                                                    <AvatarFallback className="text-xs">
                                                                        {user.first_name?.[0]}
                                                                        {user.last_name?.[0]}
                                                                    </AvatarFallback>
                                                                </Avatar>
                                                                <div>
                                                                    <p className="text-sm font-medium">
                                                                        {user.first_name} {user.last_name}
                                                                    </p>
                                                                    {user.position && (
                                                                        <p className="text-muted-foreground text-xs">{user.position}</p>
                                                                    )}
                                                                </div>
                                                            </CommandItem>
                                                        ))}
                                                    </CommandGroup>
                                                )}
                                            </CommandList>
                                        </Command>
                                    </div>
                                )}
                                <Textarea
                                    ref={textareaRef}
                                    value={newComment}
                                    onChange={handleTextareaChange}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Escape' && mentionOpen) {
                                            setMentionOpen(false);
                                            setMentionStartPos(null);
                                            return;
                                        }

                                        // "Chat" behavior: Enter sends (unless mention picker is open)
                                        if (e.key === 'Enter' && !e.shiftKey && !mentionOpen) {
                                            e.preventDefault();
                                            if (!isSubmitting && canSubmit) {
                                                handleSubmit(e);
                                            }
                                            return;
                                        }

                                        // Ctrl/Cmd+Enter to submit (kept for parity)
                                        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                                            e.preventDefault();
                                            if (!isSubmitting && canSubmit) {
                                                handleSubmit(e);
                                            }
                                        }
                                    }}
                                    placeholder="Type a message"
                                    className="bg-background focus-visible:border-border/60 h-9 min-h-9 resize-none overflow-hidden rounded-md border px-4 py-1.5 shadow-none focus-visible:ring-0"
                                    disabled={isSubmitting}
                                    rows={1}
                                />
                            </div>

                            {/* Send button (right) */}
                            <div
                                className={cn(
                                    'transition-all duration-200 ease-out',
                                    isMobile
                                        ? shouldShowSend
                                            ? 'w-9 translate-x-0 scale-100 opacity-100'
                                            : 'pointer-events-none w-0 translate-x-2 scale-95 opacity-0'
                                        : 'w-9 translate-x-0 scale-100 opacity-100',
                                )}
                                style={{ overflow: 'hidden' }}
                            >
                                <Button type="submit" size="icon" className="h-9 w-9" disabled={isSubmitting || !canSubmit}>
                                    {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* Lightbox Dialog */}
            <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
                <DialogContent className="max-h-[90vh] max-w-4xl p-0">
                    {lightboxContent && (
                        <div className="relative h-full w-full">
                            {lightboxContent.type === 'image' ? (
                                <img src={lightboxContent.url} alt={lightboxContent.name} className="h-auto max-h-[90vh] w-full object-contain" />
                            ) : (
                                <iframe src={lightboxContent.url} className="h-[90vh] w-full border-0" title={lightboxContent.name} />
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
